import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OfferSubmission } from '../../core/models';
import { ApiService } from '../../core/api.service';
import { PageSection } from '../page-section/page-section';
import { safeExternalUrl as sanitizeExternalUrl } from '../../core/url-security';

@Component({ selector: 'app-moderation-offers', standalone: true, imports: [FormsModule, PageSection], templateUrl: './moderation-offers.html', styleUrl: './moderation-offers.scss' })
export class ModerationOffers implements OnInit {
  offerSubmissions: OfferSubmission[] = [];
  selectedSubmission: OfferSubmission | null = null;
  submissionStatus = 'PENDING';
  moderationNotes = '';
  error = '';
  isLoading = false;

  constructor(private api: ApiService) {}

  ngOnInit(): void { void this.loadOfferSubmissions(); }

  async loadOfferSubmissions(): Promise<void> {
    await this.run(() => this.api.listAdminOfferSubmissions(this.submissionStatus, 50), result => {
      this.offerSubmissions = result.items;
      this.selectedSubmission = this.offerSubmissions[0] ?? null;
      this.moderationNotes = this.selectedSubmission?.moderationNotes || '';
    });
  }

  async changeSubmissionStatus(status: string): Promise<void> {
    if (status === this.submissionStatus) return;
    this.submissionStatus = status;
    await this.loadOfferSubmissions();
  }

  selectSubmission(submission: OfferSubmission): void {
    this.selectedSubmission = submission;
    this.moderationNotes = submission.moderationNotes || '';
  }

  async approveSubmission(): Promise<void> {
    if (!this.selectedSubmission) return;
    await this.run(async () => {
      await this.api.approveAdminOfferSubmission(this.selectedSubmission!.submissionId, this.moderationNotes);
      return this.api.listAdminOfferSubmissions(this.submissionStatus, 50);
    }, result => {
      this.offerSubmissions = result.items;
      this.selectedSubmission = this.offerSubmissions[0] ?? null;
      this.moderationNotes = this.selectedSubmission?.moderationNotes || '';
    });
  }

  async rejectSubmission(): Promise<void> {
    if (!this.selectedSubmission) return;
    await this.run(async () => {
      await this.api.rejectAdminOfferSubmission(this.selectedSubmission!.submissionId, this.moderationNotes);
      return this.api.listAdminOfferSubmissions(this.submissionStatus, 50);
    }, result => {
      this.offerSubmissions = result.items;
      this.selectedSubmission = this.offerSubmissions[0] ?? null;
      this.moderationNotes = this.selectedSubmission?.moderationNotes || '';
    });
  }

  async openSubmissionPdf(submission: OfferSubmission): Promise<void> {
    await this.run(() => this.api.getAdminOfferSubmissionDocument(submission.submissionId), result => {
      if (result.downloadUrl) window.open(result.downloadUrl, '_blank', 'noopener');
    });
  }

  safeExternalUrl(value: string | undefined): string {
    return sanitizeExternalUrl(value);
  }

  private async run<T>(action: () => Promise<T>, onSuccess?: (result: T) => void | Promise<void>): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    this.error = '';
    try {
      const result = await action();
      await onSuccess?.(result);
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Moderation action failed.';
    } finally {
      this.isLoading = false;
    }
  }
}
